import { api, requireAccount, withAccount } from "./core";

// --- Ambient display (always-on / standup READ surface) --------------------
// Backs GET /api/v1/galaxies/:account/ambient[?team=] (docs/ace/ambient-display.md,
// BE #980). A pure read-path: it re-derives the three-layer view (canvas / pulse /
// status) from existing observation + chronicle rows at read-time, never writing.
//
// The DTO is the anti-vanity gate enforced by TYPE: a pulse carries no number,
// count, rank or streak — only {kind, sign, subject, detail, occurred_at}. The
// canvas describes each module's CURRENT structural state (a still life), and
// status is the present snapshot (open collapse-risk count, not a trend).

// AmbientCanvasModule is one module in the slow (monthly) constellation: survival
// is the brightness, gravity the mass, concentration the stability, collapse_risk
// the negative emphasis. No person is named (structural subject only).
export interface AmbientCanvasModule {
  repo_name: string;
  module_path: string;
  domain?: string;
  survival_score?: number; // 0..100 brightness; absent when not yet observed
  gravity: number; // total surviving mass
  concentration: "SOLE_OWNER" | "CONCENTRATED" | "HEALTHY" | "FRAGMENTED";
  collapse_risk?: "ORPHANED" | "AT_RISK" | "";
}

// AmbientPulseKind is the stable string crossing JSON to the FE copy/icon map.
// The localized detail template is keyed off this (the BE `detail` is an English
// structural sentence; the FE maps kind → ja/en copy so the wall reads in locale).
export type AmbientPulseKind =
  | "first_surviving_code"
  | "module_stabilized"
  | "steward_established"
  | "collapse_risk"
  | "project_landed"
  | "chronicle_entry_added";

export type AmbientPulseSign = "positive" | "neutral" | "negative";

// AmbientActor names the person a pulse is about, disclosure-safe — using the
// SAME disclosure as module topology (the Modules page): a VISIBLE member carries
// their real id + login (the FE renders the same name the Modules page shows), a
// HARD-CLAMPED member is masked to a stable "Member-N" (label, no id/login), and a
// REDACTED member yields no actor at all. Zero new exposure vs the Modules page.
export interface AmbientActor {
  label?: string; // "Member-A" for hard-clamped; absent for visible/self
  github_user_id?: number; // real id for visible/self; absent/0 when masked
  is_self?: boolean;
  login?: string; // real github_login for a visible actor; absent when masked
  departed?: boolean; // the vacated owner has left the galaxy
}

// AmbientPulse is one discrete structural fact that landed. NUMBER-FREE by design.
export interface AmbientPulse {
  kind: AmbientPulseKind;
  sign: AmbientPulseSign;
  subject: string; // e.g. "module:repo/auth" — never a person
  detail: string; // BE English structural sentence (FE prefers its own kind→copy)
  occurred_at: string; // RFC3339; "" for a standing state (sorts last)
  actor?: AmbientActor; // disclosure-safe identity mark; absent for structural-only
  // repo_name / module_path: structural coords of the MODULE a module-kind pulse
  // is about, so the row can deep-link to /modules?module=repo/path. Absent on a
  // chronicle pulse (no module).
  repo_name?: string;
  module_path?: string;
}

// AmbientStatus is the quiet present-state readout. A snapshot, never a streak:
// collapse_risk_count is how many modules are OPEN collapse risks RIGHT NOW.
export interface AmbientStatus {
  collapse_risk_count: number;
  all_modules_stable: boolean;
  module_count: number;
}

export interface AmbientView {
  canvas: AmbientCanvasModule[];
  pulse: AmbientPulse[];
  status: AmbientStatus;
}

export const ambientApi = {
  // get reads the ambient view for a galaxy. teamId narrows to the standup (team)
  // scope; omitted = the galaxy/wall scope. The :account path segment is decorative
  // (the BE's AccountResolver resolves scope from ?account_login=, not the path),
  // so withAccount() still carries the galaxy — keeping URL the source of truth and
  // the Galaxy Boundary explicit on every call (galaxy-url-truth).
  get: (accountLogin: string, teamId?: number, mode?: "wall" | "standup") => {
    const account = requireAccount(accountLogin, "ambientApi.get");
    // mode selects the band shape (standup = actionable; wall = glance). It is
    // orthogonal to the team scope, so both params ride the same querystring.
    const params: Record<string, string> = {};
    if (teamId) params.team = String(teamId);
    if (mode) params.mode = mode;
    return api.get<AmbientView>(
      withAccount(
        `/galaxies/${encodeURIComponent(account)}/ambient`,
        account,
        Object.keys(params).length > 0 ? params : undefined,
      ),
    );
  },
};

// SuccessionEvent is one module that LOST its clear steward in the latest
// observation period — a knowledge-succession risk ("the steward is gone, who
// picks this up?"). OBSERVATION, not evaluation: the subject is a MODULE, and the
// vacated seat is named only through the SAME disclosure as module topology
// (vacated_seat is an AmbientActor — visible login, or Member-N when masked).
// departed_share / collapse_risk carry the orphaned-gravity reading; resolved is
// the close-the-loop flag (a handover was recorded for the seat, D-08). Mirrors
// services/ace/backend/app/usecase/ambient_succession.go.
export interface SuccessionEvent {
  repo_name: string;
  module_path: string;
  subject: string; // display label, e.g. "repo/auth"
  severity: "critical" | "warning";
  vacated_seat?: AmbientActor; // disclosure-safe; the owner whose departure left the seat
  departed_share: number; // 0..1 — surviving gravity held by departed owners
  collapse_risk: string; // "ORPHANED" | "AT_RISK" | ""
  occurred_at: string; // RFC3339; the period the steward was lost
  resolved: boolean; // a handover has been recorded for the vacated owner
}

export const successionApi = {
  // feed reads a galaxy's OPEN knowledge-succession risks (modules that lost their
  // clear steward this period). teamId narrows to a team's modules. Same
  // galaxy-url-truth contract as ambientApi.get — the path segment is decorative,
  // the scope rides ?account_login=, so the Galaxy Boundary is explicit per call.
  feed: (accountLogin: string, teamId?: number) => {
    const account = requireAccount(accountLogin, "successionApi.feed");
    const params: Record<string, string> = {};
    if (teamId) params.team = String(teamId);
    return api.get<SuccessionEvent[]>(
      withAccount(
        `/galaxies/${encodeURIComponent(account)}/succession`,
        account,
        Object.keys(params).length > 0 ? params : undefined,
      ),
    );
  },
};
